/**
 * The Menu's pages and their icons, in a module of their own so the bottom bar
 * (MobileNav, mounted in the ROOT layout) can read them without pulling the
 * whole drawer into every page's bundle. MobileMenu re-exports both.
 *
 * The Liquid Glass bar (variant B) shows the current page's icon in the circle
 * the tabs fold into; reading it from here means a page cannot look one way in
 * the Menu and another in the bar.
 */
import {
  BarChart3,
  BellRing,
  Briefcase,
  CalendarCheck,
  ClipboardList,
  Clock,
  FileText,
  Flame,
  Gauge,
  Gift,
  Headset,
  Inbox as InboxIcon,
  ListChecks,
  Mail,
  MapPin,
  Repeat,
  Scale,
  Settings as SettingsIcon,
  ShieldCheck,
  Star,
  Package,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PARTS_NAV_PERMISSION, hasNavPermission } from '@/lib/skills/nav';
import { inboxEnabledFor } from '@/lib/inbox/beta';
import { isCoreSkill } from '@/lib/skills/core';

/** The Menu's fixed rows (everything that is not a skill), by path. */
export const MENU_ROW_ICONS = {
  '/actions': InboxIcon,
  '/inbox': Mail,
  '/jobs': Clock,
  '/notepad': FileText,
  '/properties': MapPin,
  '/engineers': Users,
  '/settings': SettingsIcon,
  '/settings/referrals/referrals': Gift,
  '/admin': ShieldCheck,
} as const satisfies Record<string, LucideIcon>;

export interface SkillItem {
  slug: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /**
   * REQUIRED permission gate — the entry is hidden unless
   * `userPermissions[permission]` is true. Mandatory for the same reason it is
   * mandatory on SkillNavItem: seven entries here had none until 2026-08-14,
   * so a subcontractor's Menu listed Quotes, Invoicing, Revenue, Work Orders,
   * Projects, Reviews and Live Map. Keep this list in step with
   * SKILL_NAV_LIST — `skill-nav-permissions.test.ts` asserts they agree.
   */
  permission: string | readonly string[];
  /**
   * Listed only on a phone in Liquid Glass. A row Tom accepted for the glass
   * Menu (docs/glass-batch-4.md) that variant A has never had; variant A's
   * Menu stays exactly as it was.
   */
  glassOnly?: true;
  /**
   * The skill whose switch lists this row, when the row is a page OF a skill
   * rather than a skill of its own. Defaults to `slug`. CIS taken off you is a
   * report under Revenue: `view_revenue_reports` is that skill's key, so the
   * row follows the skill as the key does.
   */
  skill?: string;
  /**
   * Listed only for an account that works as a subcontractor
   * (`SidebarData.doesSubbieWork`). CIS taken off you means nothing to a firm
   * nobody deducts CIS from (Tom, 16 Oct).
   */
  subbieOnly?: true;
  /**
   * The name on the bottom bar, when the Menu's is too long for a tab. Tab
   * labels are 12px and never shrunk to fit (glass-type.md), so a bar label
   * stays at nine characters or fewer — `bar-slots.test.ts` holds it.
   */
  barLabel?: string;
}

/**
 * Mirrors SKILL_NAV_LIST + CATEGORY_ORDER in `src/lib/skills/nav.ts` but uses
 * lucide icons matching the rest of the mobile design system.
 *
 * EXPORTED purely so `skill-nav-permissions.test.ts` can assert the two lists
 * agree on slug → permission. Hand-mirrored lists drift — the Service Reminders
 * entry was missing here for a month after the skill shipped — and when the
 * thing that drifts is the permission gate, the drift is a data leak on one
 * platform only, which is the hardest kind to notice.
 */
export const SKILLS_BY_CATEGORY: Record<string, SkillItem[]> = {
  Trade: [
    { slug: 'work-orders', label: 'Work Orders', barLabel: 'Orders', href: '/work-orders', icon: ClipboardList, permission: 'view_work_orders' },
    { slug: 'gas-certificates', label: 'Certificates', barLabel: 'Certs', href: '/gas-certs', icon: Flame, permission: 'view_gas_certs' },
    {
      slug: 'gas-calculators',
      label: 'Calculators',
      barLabel: 'Calcs',
      href: '/gas-calculators',
      icon: Gauge,
      permission: 'use_gas_calculators',
    },
    {
      // 🚨 `use_custom_forms`, the ENGINEER's key, and it changed on 4 September
      // when the page did. It used to be the builder's key with the comment
      // "this is the builder, not the filling-in" — which was true of the old
      // page, because the old page listed TEMPLATES. It lists completed forms
      // now, the way /gas-certs lists issued certificates, and authoring moved
      // behind a Settings button gated on `manage_form_templates` inside it.
      // So the nav gate is "may you have anything to do with forms" and the
      // narrower key guards the one control that writes a template.
      slug: 'form-builder',
      label: 'Forms',
      href: '/forms',
      icon: ListChecks,
      permission: 'use_custom_forms',
    },
    // Parts — both halves of Parts & Van Stock; van stock is a tab of it.
    // Same keys as the desktop row (PARTS_NAV_PERMISSION).
    { slug: 'parts-tracker', label: 'Parts', href: '/parts', icon: Package, permission: PARTS_NAV_PERMISSION },
  ],
  Operations: [
    { slug: 'live-map', label: 'Live Map', barLabel: 'Map', href: '/map', icon: MapPin, permission: 'view_live_map' },
    { slug: 'projects', label: 'Projects', href: '/projects', icon: Briefcase, permission: 'view_projects' },
    { slug: 'reviews', label: 'Reviews', href: '/reviews', icon: Star, permission: 'view_reviews' },
    {
      // Was missing here since the skill shipped (2026-07-05), so mobile users
      // could not reach /service-reminders from the menu at all.
      slug: 'service-reminders',
      label: 'Service Reminders',
      barLabel: 'Reminders',
      href: '/service-reminders',
      icon: BellRing,
      permission: 'view_service_reminders',
    },
    {
      slug: 'on-time-tracker',
      label: 'On Time Tracker',
      barLabel: 'On time',
      href: '/on-time-tracker',
      icon: Clock,
      permission: 'use_on_time_tracker',
    },
    {
      slug: 'voice-receptionist',
      label: 'Receptionist',
      barLabel: 'Reception',
      href: '/voice-receptionist',
      icon: Headset,
      permission: 'manage_voice_receptionist',
    },
    {
      // Not in variant A's Menu, where the page was reachable only from the
      // desktop's sidebar, Skills or a card (docs/glass-batch-4.md).
      slug: 'online-booking',
      label: 'Online booking',
      barLabel: 'Bookings',
      href: '/online-booking',
      icon: CalendarCheck,
      permission: 'manage_online_booking',
      glassOnly: true,
    },
    {
      slug: 'timesheets',
      label: 'Timesheets',
      barLabel: 'Hours',
      href: '/timesheets',
      icon: Clock,
      // Either hours key — see SKILL_NAV_LIST's timesheets entry.
      permission: ['view_all_hours', 'view_own_hours'],
    },
  ],
  Finance: [
    { slug: 'quotes', label: 'Quotes', href: '/quotes', icon: FileText, permission: 'view_quotes' },
    { slug: 'invoicing', label: 'Invoicing', barLabel: 'Invoices', href: '/invoices', icon: FileText, permission: 'view_invoices' },
    { slug: 'revenue-reports', label: 'Revenue', href: '/revenue-reports', icon: BarChart3, permission: 'view_revenue_reports' },
    {
      slug: 'service-plans',
      label: 'Service Plans',
      barLabel: 'Plans',
      href: '/service-plans',
      icon: Repeat,
      permission: 'view_service_plans',
    },
    {
      // Not in variant A's Menu, where the page had no link anywhere in the app
      // (docs/glass-batch-4.md). Tom's name for what HMRC calls CIS suffered:
      // "CIS" alone clashes with the CIS taken off your OWN subcontractors.
      // Same key as the page (`reports/cis-suffered/page.tsx`).
      slug: 'cis-taken-off-you',
      skill: 'revenue-reports',
      label: 'CIS taken off you',
      barLabel: 'CIS',
      href: '/reports/cis-suffered',
      icon: Scale,
      permission: 'view_revenue_reports',
      glassOnly: true,
      subbieOnly: true,
    },
  ],
};



/**
 * What decides whether a Menu row is drawn for this person, on this phone.
 * The Menu and the bottom bar's choices (`bar-slots.ts`) both ask through the
 * two functions below, so a page cannot be offered for the bar to somebody
 * the Menu would hide it from — two hand-kept copies of a gate is how a gate
 * leaks on one surface only.
 */
export interface MenuGateContext {
  perms: Record<string, boolean>;
  /** Skills installed on the active account. */
  skills: readonly string[];
  /** Owns or shares a mailbox (the Inbox row's second arm). */
  hasMailbox: boolean;
  /** Liquid Glass on a phone (`glassOnly` rows). */
  glass: boolean;
  /** The App Store / Play build, where Live Map is never listed. */
  native: boolean;
  /** The account works as a subcontractor (`subbieOnly` rows). */
  doesSubbieWork: boolean;
}

const holds = (ctx: MenuGateContext, key: string) => ctx.perms[key] === true;

/** The fixed rows' gates, by path. Same keys as the desktop Sidebar. */
export const MENU_ROW_GATES = {
  // The page Home's Needs you lands on (Tom, 2026-10-12).
  '/actions': (ctx: MenuGateContext) => holds(ctx, 'view_actions'),
  // An owner sees Inbox straight away so there is somewhere to connect from;
  // everyone else earns the row by connecting a mailbox of their own.
  '/inbox': (ctx: MenuGateContext) =>
    inboxEnabledFor(ctx.skills)
    && holds(ctx, 'view_inbox')
    && (holds(ctx, 'send_email_replies') || ctx.hasMailbox),
  // Both count: an engineer holds only view_own_jobs and still needs the page.
  '/jobs': (ctx: MenuGateContext) => holds(ctx, 'view_all_jobs') || holds(ctx, 'view_own_jobs'),
  '/notepad': (ctx: MenuGateContext) => holds(ctx, 'use_notepad'),
  // Properties is core, not a skill, gated on view_clients like the Sidebar.
  '/properties': (ctx: MenuGateContext) => holds(ctx, 'view_clients'),
  '/engineers': (ctx: MenuGateContext) => holds(ctx, 'view_team'),
} as const satisfies Partial<Record<keyof typeof MENU_ROW_ICONS, (ctx: MenuGateContext) => boolean>>;

/** Whether a skill's row is listed in the Menu for this person. */
export function skillRowVisible(s: SkillItem, ctx: MenuGateContext): boolean {
  // Quotes and Invoicing are core: permission alone decides them.
  if (!isCoreSkill(s.skill ?? s.slug) && !ctx.skills.includes(s.skill ?? s.slug)) return false;
  // Live Map is hidden in native binaries (App Store guideline 2.5.4 — a map
  // of engineer locations reads as employee tracking).
  if (s.slug === 'live-map' && ctx.native) return false;
  if (s.glassOnly && !ctx.glass) return false;
  if (s.subbieOnly && !ctx.doesSubbieWork) return false;
  // Permission-gated entries are hidden unless the role was granted the
  // permission at skill install time via SkillPermissionsWizard. No
  // truthiness check on `s.permission` — it is required, and
  // `if (s.permission && …)` is exactly what silently skipped the gate for
  // every entry that forgot to declare one. An array means any ONE of the
  // keys is enough (Timesheets: all-hours or own).
  return hasNavPermission(s.permission, (k) => holds(ctx, k));
}
